import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Sky } from './Sky';
import { TabBar } from './TabBar';

/** Sub-pixel slack, so a fractional scroll position still counts as the end. */
const EDGE = 1;

export function Screen({
  children,
  header,
  foot,
  nav = false,
  quietSky = false,
  centre = false,
}: {
  children: ReactNode;
  /**
   * The screen's own bar. It sits outside the scroll area, so it stays where
   * it is while the record scrolls under it — a header passed as a child
   * instead scrolls away with everything else.
   */
  header?: ReactNode;
  foot?: ReactNode;
  nav?: boolean;
  /** Drop the moon and clouds on screens with no hero card to hide behind. */
  quietSky?: boolean;
  /**
   * Let a child claim the leftover height. Without it the scroll area is a
   * block, `flex: 1` on the child means nothing, and a column that asks to be
   * centred sits at the top with the rest of the screen empty below it.
   */
  centre?: boolean;
}) {
  const scroll = useRef<HTMLDivElement>(null);
  /**
   * Which ends of the record are still on screen. A bar only casts a shadow
   * once there is something passing under it: at the top of the record nothing
   * is behind the header, at the bottom nothing is behind the tab bar, and a
   * shadow there would claim a depth the screen does not have. A screen whose
   * content fits is at both ends at once, so it carries neither.
   */
  const [at, setAt] = useState({ top: true, bottom: true });

  const measure = useCallback(() => {
    const el = scroll.current;
    if (!el) return;
    const top = el.scrollTop <= EDGE;
    const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - EDGE;
    // Same value, same object: React bails out of the render, which is what
    // makes it safe to measure on every one of them.
    setAt((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  // After every render, because the thing that changes most often is the
  // content's own height — a record that grew a row, a sheet that opened —
  // and neither a scroll nor a resize of the box itself reports that.
  useLayoutEffect(measure);

  useEffect(() => {
    const el = scroll.current;
    if (!el) return;
    el.addEventListener('scroll', measure, { passive: true });
    // The box changes height without the window doing so: the keyboard, and
    // a phone's URL bar collapsing under a `dvh` layout.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    observer?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  return (
    <div
      className={`screen${at.top ? '' : ' lifted-top'}${at.bottom ? '' : ' lifted-bottom'}`}
      data-testid="screen"
    >
      <Sky quiet={quietSky} />
      {header ? <div className="screen-head">{header}</div> : null}
      <div
        ref={scroll}
        className={`screen-scroll${centre ? ' screen-centre' : ''}${header ? ' has-head' : ''}`}
      >
        {children}
      </div>
      {foot ? <div className="screen-foot">{foot}</div> : null}
      {nav ? <TabBar /> : null}
    </div>
  );
}
